import type { FC } from 'react'

import {
  Button,
  ButtonColors,
  ButtonSizes,
  ButtonVariants
} from '@inno-ui/button'
import {
  BookOpenLineIcon,
  ChartBarVerticalLineIcon,
  ClipboardCheckmarkLineIcon,
  ClockLineIcon,
  CoinsEuroLineIcon,
  DocumentBadgeCheckmarkLineIcon,
  EllipsisHorizontalCircleLineIcon,
  GearLineIcon,
  GridLineIcon,
  HeartRateCircleLineIcon,
  LaptopBadgeGearLineIcon,
  PersonOnPersonLineIcon,
  PersonSpeechBubbleLineIcon,
  StarsLineIcon
} from '@inno-ui/icons'
import type { BasicIconProps } from '@inno-ui/icons/lib/Icons.types'
import { useTranslation } from 'react-i18next'

import { SidebarAppButton } from '../AppsSidebar/SidebarAppButton'
import styles from './CategoryNavigation.module.scss'
import type { CategoryNavigationProps } from './CategoryNavigation.types'

const categoryIcons: Record<string, FC<BasicIconProps>> = {
  'tasks-projects': ClipboardCheckmarkLineIcon,
  'time-scheduling': ClockLineIcon,
  'sales-customers': ChartBarVerticalLineIcon,
  'finance-funding': CoinsEuroLineIcon,
  'people-teams': PersonOnPersonLineIcon,
  communication: PersonSpeechBubbleLineIcon,
  'knowledge-learning': BookOpenLineIcon,
  'operations-monitoring': LaptopBadgeGearLineIcon,
  'personal-lifestyle': HeartRateCircleLineIcon,
  utilities: GearLineIcon
}

export const CategoryNavigation = ({
  categories,
  activeCategoryId,
  onSelect,
  onCreateWidget,
  showReview,
  isReviewActive,
  onOpenReview
}: CategoryNavigationProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'widgets.marketplace'
  })
  const { t: tAi } = useTranslation('translation', {
    keyPrefix: 'widgets.aiAgent'
  })
  return (
    <div className={styles.sidebar}>
      <nav className={styles.navigation} aria-label={t('categories')}>
        {[{ id: null, name: t('all') }, ...categories].map(category => (
          <SidebarAppButton
            key={category.id ?? 'all'}
            icon={
              category.id === null
                ? GridLineIcon
                : (categoryIcons[category.id] ??
                  EllipsisHorizontalCircleLineIcon)
            }
            label={category.name}
            isActive={activeCategoryId === category.id}
            onClick={() => onSelect(category.id)}
          />
        ))}
      </nav>
      <div className={styles.footer}>
        {[false, true].map(compact => (
          <div
            key={String(compact)}
            className={compact ? styles.compactAction : styles.fullAction}
          >
            {onCreateWidget && (
              <div className={styles.createAction}>
                <Button
                  icon={StarsLineIcon}
                  label={tAi('emptyCta')}
                  aria-label={tAi('emptyCta')}
                  title={tAi('emptyCta')}
                  isHiddenLabel={compact}
                  color={ButtonColors.Gradient1}
                  variant={ButtonVariants.Dark}
                  size={ButtonSizes.M}
                  width='100%'
                  onClick={onCreateWidget}
                />
              </div>
            )}
            {showReview && (
              <Button
                icon={DocumentBadgeCheckmarkLineIcon}
                label={tAi('review_section_title')}
                aria-label={tAi('review_section_title')}
                aria-pressed={isReviewActive}
                title={tAi('review_section_title')}
                isHiddenLabel={compact || !!onCreateWidget}
                variant={
                  isReviewActive ? ButtonVariants.Dark : ButtonVariants.Grey
                }
                size={ButtonSizes.M}
                width={onCreateWidget ? undefined : '100%'}
                onClick={onOpenReview}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
